/**
 * ПОЛОСА «МНЕНИЕ АВТОРА» — мнение автора канала о посте и действия с ним, под постом (AGI
 * Boardroom, build spec 2026-11-02 §4).
 *
 * ЧТО ЭТО. Автор канала пишет своё мнение простым текстом, сервис генерации его чистит и вставляет
 * в пост без пометки; мнение хранится отдельно от версий и переносится в каждую следующую
 * переделку дословно (`api/author-opinion.ts`). Полоса показывает очищенный текст, кто и когда
 * его оставил и стоит ли он в ЭТОЙ версии дословно — «есть в тексте» / «нет в тексте».
 *
 * ОРГАН ЭКРАНА, А НЕ ПЯТАЯ КНОПКА ПОЛОСЫ РЕШЕНИЯ. Правая группа полосы решения подогнана по
 * ширине под четыре решения (`DecisionPanel.module.css`), и порядок кнопок закреплён тестами;
 * мнение к тому же — не исход рассмотрения, а правка поста. Поэтому оно живёт рядом с полосами
 * картинки и файла, во всех трёх режимах экрана.
 *
 * ТОЛЬКО У БРЕНДА С ВОЗМОЖНОСТЬЮ. Полосы нет вовсе, пока список брендов не подтвердил
 * `author_opinion.allowed` (`brandAuthorOpinion`): правило «пустого состояния у полосы нет»
 * действует там, где возможность есть, а у GeoBoost её нет. Кнопки гаснут на месте, с причиной в
 * имени и подсказке (`aria-disabled`, как «Переделать…»): без сервиса генерации — его причиной, у
 * версии, которую нельзя отправить на переделку, — причиной по состоянию (`opinionLockReason`).
 *
 * ПРАВКА НЕ ТЕРЯЕТСЯ. Отправка мнения новой версии не рождает — её родит писатель, — поэтому
 * экран после неё перечитывается без сброса набранной правки (`onChanged` от `DraftPanel`).
 */

import { type JSX, useCallback, useState } from 'react';
import { agentsOffReason, brandAuthorOpinion, offLabel, useBrands } from '../../brand.tsx';
import { shortMsk } from '../../components/format.ts';
import type { OpinionStripProps } from '../contracts.ts';
import { AuthorOpinionDialog, type AuthorOpinionDialogMode } from './AuthorOpinionDialog.tsx';
import styles from './OpinionStrip.module.css';
import {
  opinionHeadline,
  opinionHeadlineTitle,
  opinionLastNote,
  opinionLockReason,
  opinionViewOf,
} from './opinionState.ts';

export function OpinionStrip({ model, onChanged }: OpinionStripProps): JSX.Element | null {
  const brands = useBrands();
  const [dialog, setDialog] = useState<AuthorOpinionDialogMode | null>(null);
  // Функция живёт всю жизнь полосы: `Modal` держит фокус эффектом от `onClose`, и новая функция
  // на каждой перерисовке уводила бы фокус с поля мнения.
  const closeDialog = useCallback((): void => {
    setDialog(null);
  }, []);

  const allowed = brandAuthorOpinion(brands, model.brand);
  if (allowed === null) return null;

  const view = opinionViewOf(model);
  // Мнение вставляет сервис генерации, переделкой: без него и у версии, которую нельзя
  // отправить на переделку, кнопки гаснут. Сервис — первым: его причину не снять ничем на экране.
  const reason =
    agentsOffReason(brands, model.brand, ['generation']) ?? opinionLockReason(model.status);
  const headlineTitle = opinionHeadlineTitle(view);
  const lastNote = opinionLastNote(view);
  const opinion = view.state === 'none' ? null : view.opinion;

  const action = (label: string, mode: AuthorOpinionDialogMode): JSX.Element => {
    const name = label.replace(/…$/, '');
    return reason === null ? (
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          setDialog(mode);
        }}
      >
        {label}
      </button>
    ) : (
      <button
        type="button"
        className="btn btn-sm btn-dis"
        aria-disabled="true"
        aria-label={offLabel(name, reason)}
        title={offLabel(name, reason)}
      >
        {label}
      </button>
    );
  };

  /*
   * Поле диалога: при «Изменить» — мнение, как его написали (очищенное правят словами автора, а
   * не очистки); при «Добавить» после несостоявшейся вставки — её текст, чтобы повторить, не
   * набирая заново.
   */
  const initialText =
    dialog === 'change'
      ? (opinion?.raw ?? '')
      : view.state === 'none' && view.last?.state === 'cancelled'
        ? view.last.raw
        : '';

  return (
    <section className={styles['strip']} aria-label="Мнение автора" data-testid="opinion-strip">
      <div className={styles['head']}>
        <span className="eyebrow">Мнение автора</span>
        <span className="meta" title={headlineTitle ?? undefined} data-testid="opinion-state">
          {opinionHeadline(view)}
        </span>
      </div>

      {opinion === null ? null : (
        <>
          {/* До вставки очищенного текста нет — показано мнение, как его написали. */}
          <blockquote className={styles['quote']} data-testid="opinion-text">
            {opinion.cleaned ?? opinion.raw}
          </blockquote>
          <span className="meta" data-testid="opinion-author">
            {`${opinion.submitted_by_login} · ${shortMsk(opinion.created_at)}`}
          </span>
        </>
      )}

      {lastNote === null ? null : (
        <span className="meta" data-testid="opinion-last">
          {lastNote}
        </span>
      )}

      <div className={styles['actions']}>
        {opinion === null ? (
          action('Добавить мнение…', 'add')
        ) : (
          <>
            {action('Изменить мнение…', 'change')}
            {action('Убрать мнение…', 'remove')}
          </>
        )}
      </div>

      {dialog === null ? null : (
        <AuthorOpinionDialog
          model={model}
          mode={dialog}
          initialText={initialText}
          maxCodepoints={allowed.maxCodepoints}
          onChanged={onChanged}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
